import { useEffect, useReducer, useState } from 'react';

import { MotorplayContext } from './Context';
import { motorplayReducer, TYPES } from './reducer';
import {
  getPlans as getPlansRequest,
  createSubscription as createSubscriptionRequest,
  getSubscription as getSubscriptionMP,
  cancelSubscription as cancelSubscriptionMP,
} from '../services/mercadopagoService';

import {
  getPlans as getPlansRequestStripe,
  createSubscription as createSubscriptionRequestStripe,
  createPaymentLink as createPaymentLinkRequestStripe,
  getSubscription as getSubscriptionRequestStripe,
  cancelSubscription as cancelSubscriptionStripe,
} from '../services/stripeService';

const INITIAL_STATE = {
  plans: null,
  selectedPlan: null,
  error: null,
  errorCoupon: null,
  complete: false,
  completeByCoupon: false,
  loading: false,
  subscriptionInfo: null,
};

export const MotorplayProvider = (props) => {
  const [state, dispatch] = useReducer(motorplayReducer, INITIAL_STATE);
  const [openModal, setOpenModal] = useState(false);
  const [type, setType] = useState(null);

  const {
    selectedPlan,
    complete,
    error,
    completeByCoupon,
    completeByCancelSubscription,
    errorCancelSubscription,
    plans,
  } = state;

  const getPlans = (token, country) => {
    dispatch({ type: TYPES.SET_LOADING });

    if (country.toLowerCase() === 'ar') {
      getPlansRequest(token)
        .then((resp) => {
          dispatch({ type: TYPES.SET_PLANS, payload: resp });
        })
        .catch((error) => {
          dispatch({ type: TYPES.SET_ERROR, payload: error.message });
        });

      return;
    }

    getPlansRequestStripe(token)
      .then((resp) => {
        dispatch({ type: TYPES.SET_PLANS, payload: resp });
      })
      .catch((error) => {
        dispatch({ type: TYPES.SET_ERROR, payload: error.message });
      });
  };

  const resetError = () => {
    dispatch({ type: TYPES.RESET_ERROR });
  };

  const resetErrorCancelSubscription = () => {
    dispatch({ type: TYPES.RESET_ERROR_CANCEL_SUBSCRIPTION });
  };

  const resetSuccessCancelSubscription = () => {
    dispatch({ type: TYPES.RESET_SUCCESS_CANCEL_SUBSCRIPTION });
  };

  const setPlanToBuy = (plan) => {
    dispatch({ type: TYPES.SET_PLAN_TO_BUY, payload: plan });
  };

  const createSubscriptionMercadopago = (token, tokenCard) => {
    dispatch({ type: TYPES.SET_LOADING });
    createSubscriptionRequest(token, { token: tokenCard, planId: selectedPlan })
      .then(() => {
        dispatch({ type: TYPES.SET_COMPLETE });
      })
      .catch((error) => {
        dispatch({ type: TYPES.SET_ERROR, payload: error.message });
      });
  };

  const createSubscriptionStripe = (token, sessionId) => {
    dispatch({ type: TYPES.SET_LOADING });
    createSubscriptionRequestStripe(token, { sessionId })
      .then(() => {
        dispatch({ type: TYPES.SET_COMPLETE });
      })
      .catch((error) => {
        dispatch({ type: TYPES.SET_ERROR, payload: error.message });
      });
  };

  const createPaymentLinkStripe = async (token, redirectUrl) => {
    dispatch({ type: TYPES.SET_LOADING });

    try {
      const url = createPaymentLinkRequestStripe(token, {
        planId: selectedPlan,
        redirectUrl,
      });
      dispatch({ type: TYPES.RESET_LOADING });
      return url;
    } catch (error) {
      dispatch({ type: TYPES.SET_ERROR, payload: error.message });
    }
  };

  const resetErrorCoupon = () => {
    dispatch({ type: TYPES.RESET_ERROR_COUPON });
  };

  const getSubscription = (token, country) => {
    dispatch({ type: TYPES.SET_LOADING });

    if (country.toLowerCase() === 'ar') {
      getSubscriptionMP(token)
        .then((resp) => {
          const removeCancelledSubscriptionMP = resp.filter(subs => subs.status === 'authorized');
          dispatch({ type: TYPES.SET_SUBSCRIPTION, payload: removeCancelledSubscriptionMP });
        })
        .catch((error) => {
          dispatch({
            type: TYPES.SET_ERROR_SUBSCRIPTION,
            payload: error.message,
          });
        });

      return;
    }

    getSubscriptionRequestStripe(token)
      .then((resp) => {
        const removeCancelledSubscription = resp.filter(subs => subs.status === 'active');
        dispatch({ type: TYPES.SET_SUBSCRIPTION, payload: removeCancelledSubscription });
      })
      .catch((error) => {
        dispatch({
          type: TYPES.SET_ERROR_SUBSCRIPTION,
          payload: error.message,
        });
      });

    return;
  };

  const sucessByCoupon = () => {
    resetErrorCoupon();
    dispatch({ type: TYPES.SET_COMPLETE_BY_COUPON });
  };
  
  const setLoading = (loading = true) => {
    if (!loading) {
      dispatch({ type: TYPES.RESET_LOADING });
      return;
    }
    
    dispatch({ type: TYPES.SET_LOADING });
  };

  const cancelSubscription = (token, country, subscriptionId) => {
    dispatch({ type: TYPES.SET_LOADING });

    if (country.toLowerCase() === 'ar') {
      cancelSubscriptionMP(token, subscriptionId)
        .then((resp) => {
          dispatch({ type: TYPES.CANCEL_SUBSCRIPTION, payload: resp });
          return resp;
        })
        .catch((error) => {
          dispatch({
            type: TYPES.SET_ERROR_CANCEL_SUBSCRIPTION,
            payload: error.message,
          });
        });

      return;
    }

    cancelSubscriptionStripe(token, subscriptionId)
      .then((resp) => {
        dispatch({ type: TYPES.CANCEL_SUBSCRIPTION, payload: resp });
      })
      .catch((error) => {
        dispatch({
          type: TYPES.SET_ERROR_CANCEL_SUBSCRIPTION,
          payload: error.message,
        });
      });

    return;
  };

  useEffect(() => {
    if (
      !complete &&
      !error &&
      !completeByCoupon &
        !errorCancelSubscription &
        !completeByCancelSubscription
    )
      return;

    if (complete) {
      setType('success');
      setOpenModal(true);
    }

    if (completeByCoupon) {
      setType('successCoupon');
      setOpenModal(true);
    }

    if (completeByCancelSubscription) {
      setType('successCanceledSubscription');
      setOpenModal(true);
    }

    if (errorCancelSubscription) {
      setType('failedCanceledSubscription');
      setOpenModal(true);
    }

    if (error) {
      setType('failed');
      setOpenModal(true);
    }
  }, [
    complete,
    error,
    completeByCoupon,
    errorCancelSubscription,
    completeByCancelSubscription,
  ]);

  return (
    <MotorplayContext.Provider
      value={{
        ...state,
        openModal,
        type,
        getPlans,
        createSubscriptionMercadopago,
        createSubscriptionStripe,
        setPlanToBuy,
        resetError,
        createPaymentLinkStripe,
        setOpenModal,
        setType,
        resetErrorCoupon,
        getSubscription,
        cancelSubscription,
        resetErrorCancelSubscription,
        resetSuccessCancelSubscription,
        sucessByCoupon,
        setLoading
      }}
      {...props}
    />
  );
};
